import React, { FunctionComponent, useRef } from 'react';

import { ReadFile } from '@paragon/client-tools';
import { hasFreeSubscription } from '@paragon/login-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { beforeUploadFiles, uploadFiles } from '../../store/actions/uploadFilesActions';
import { useAppDispatch, useAppSelector } from '../hooks';
import DefaultFileUploader from './DefaultFileUploader';

type Variant = 'addToStation' | 'upload' | 'uploadAndAnalyze';
type Client = 'mobile' | 'desktop';

interface Props {
  variant: Variant;
  client: Client;
}

function getDisplayText(variant: Variant) {
  switch (variant) {
    case 'addToStation':
      return 'Add to Station';

    case 'upload':
      return 'Upload';

    case 'uploadAndAnalyze':
      return 'Upload and Analyze';

    default:
      assertExhaustiveSwitch(variant, 'Invalid HeaderFileUploader variant');
  }
}

function getFileTypes(variant: Variant) {
  switch (variant) {
    case 'addToStation':
      return '.tre,.ans,.tps,.tdltruss,.zip,.ddp,.pdf,.cnt,.seq';

    case 'upload':
      return `.tre,.ans,.tps,.of,.clearspan,.tdltruss,.kxf,.kxr,.pcd,.zip,.ddp,.pic,.dxf,.xml,.json,.ifc,.pdf,.png,.jpg,.jpeg,.webp,.gif`;

    case 'uploadAndAnalyze':
      return `.tre,.ans,.tps,.of,.clearspan,.tdltruss,.kxf,.kxr,.pcd,.zip,.ddp,.pic,.dxf,.xml,.json,.ifc,.pdf`;

    default:
      assertExhaustiveSwitch(variant, 'Invalid HeaderFileUploader variant');
  }
}

function useOnUpload(variant: Variant) {
  const dispatch = useAppDispatch();
  const spinnerId = useRef<number | null>(null);

  return [
    () => {
      spinnerId.current = dispatch(beforeUploadFiles());
    },
    (files: ReadFile[]) =>
      void dispatch(
        uploadFiles(files, {
          beforeUploadSpinnerId: spinnerId.current,

          addToStation: variant === 'addToStation',
          persist: variant === 'addToStation' || variant.startsWith('upload'),
          analyze: variant === 'uploadAndAnalyze',
        }),
      ),
  ] as const;
}

const HeaderFileUploader: FunctionComponent<Props> = ({ variant, client }) => {
  const isFreeUser = useAppSelector(hasFreeSubscription);

  const [onBeforeUpload, onUpload] = useOnUpload(variant);

  return (
    <DefaultFileUploader
      client={client}
      disabled={isFreeUser}
      displayText={getDisplayText(variant)}
      fileTypes={getFileTypes(variant)}
      beforeUploadFiles={onBeforeUpload}
      uploadFiles={onUpload}
    />
  );
};

export default HeaderFileUploader;
